import { useState, type ReactElement } from 'react';
import type { PublicationDto } from '@gr/core/distribution/publishing/dto';
import { useT } from '../../../../app/di';
import { toLocalInputValue } from '../../../../shared/format';
import { Button } from '../../../../ui-kit/Button/Button';
import { BareInput } from '../../../../ui-kit/Field/Field';
import { PublicationLine } from '../PublicationLine/PublicationLine';

export interface PlannedPublicationsProps {
  publications: readonly PublicationDto[];
  timezone: string;
  editable: boolean;
  pending: boolean;
  /** Новое время — в поясе блога, «ГГГГ-ММ-ДДTчч:мм». */
  onReschedule(publication: PublicationDto, localTime: string): Promise<boolean>;
  onUnschedule(publication: PublicationDto): void;
}

/** SCH-UC-6, SCH-RULE-3: запланированные публикации поста; перенос и снятие. */
export function PlannedPublications({ publications, timezone, editable, pending, onReschedule, onUnschedule }: PlannedPublicationsProps): ReactElement {
  return (
    <div className="u-stack">
      {publications.map((publication) => (
        <PlannedRow key={publication.id} publication={publication} timezone={timezone} editable={editable} pending={pending} onReschedule={onReschedule} onUnschedule={onUnschedule} />
      ))}
    </div>
  );
}

function PlannedRow({ publication, timezone, editable, pending, onReschedule, onUnschedule }: Omit<PlannedPublicationsProps, 'publications'> & { publication: PublicationDto }): ReactElement {
  const { t } = useT();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(() => toLocalInputValue(publication.plannedAt ?? new Date().toISOString(), timezone));
  return (
    <div className="u-stack u-gap-1">
      <PublicationLine
        publication={publication}
        timezone={timezone}
        actions={
          editable ? (
            <>
              <Button size="sm" variant="ghost" onClick={() => setEditing(!editing)}>
                {t('scheduling.panel.reschedule')}
              </Button>
              <Button size="sm" variant="ghost" disabled={pending} onClick={() => onUnschedule(publication)}>
                {t('scheduling.panel.unschedule')}
              </Button>
            </>
          ) : null
        }
      />
      {editing ? (
        <div className="u-row">
          <div className="u-min-field">
            <BareInput label={t('scheduling.panel.newTime')} type="datetime-local" value={value} onChange={(e) => setValue(e.target.value)} />
          </div>
          <Button size="sm" variant="primary" loading={pending} onClick={async () => (await onReschedule(publication, value)) && setEditing(false)}>
            {t('scheduling.panel.apply')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
